import type { ReactNode } from 'react';
import { cn } from './cn.js';

export type ConversationSide = 'meu' | 'outro' | 'sistema';
export type ConversationTone = 'padrao' | 'nota' | 'lideranca';

/**
 * Uma mensagem de conversa, desenhada como CHAT (23/09/2026).
 *
 * Pedido do dono: "de um lado as mensagens enviadas e do outro as recebidas,
 * como um bate-papo". A peça é uma só para as duas SPAs — até ali a interna
 * tinha `MessageNaRow` e o portal um `<li>` à mão, cada um com a sua ideia de
 * mensagem.
 *
 * ## O lado é de quem OLHA
 *
 * `meu` à direita, `outro` à esquerda — como o WhatsApp. Quem decide o que é
 * "meu" é a tela, com o dado que ela tem: na interna, o autor contra o usuário
 * da sessão; no portal, o autor associado. `sistema` (a abertura, a mudança de
 * situação, o encaminhamento) fica no meio, pequeno e sem balão: não é
 * ninguém falando.
 *
 * ## A cor diz de quem é; o selo diz o que é
 *
 * - `meu`: fundo laranja claro (`primaria-suave`), sem borda.
 * - `outro`: fundo neutro com borda.
 * - `tone="nota"` — a nota interna — é âmbar dos DOIS lados, e vence a cor do
 *   lado: visibilidade manda, é o aviso que impede a conversa da equipe de
 *   parecer uma resposta ao cliente. O selo "Nota interna" vem em `badges`,
 *   em texto: estado nunca é só cor.
 * - `tone="lideranca"` ganha borda laranja, e o selo "Liderança".
 *
 * Borda sempre inteira, nunca lateral — `borda-lateral-colorida` reprova.
 *
 * ## Continua sendo um `<li>`, com o texto e os selos DENTRO
 *
 * Os percursos no navegador acham a mensagem como `listitem` pelo texto dela
 * e procuram "Nota interna" ali dentro (`internal.tickets.spec.ts`,
 * `internal.departments.spec.ts`). A lista em volta é de quem chama.
 */
export function ConversationMessage({
  side,
  tone = 'padrao',
  author,
  timestamp,
  badges,
  avatar,
  children,
  footer,
}: {
  side: ConversationSide;
  tone?: ConversationTone;
  /** o nome que aparece acima do balão — "Você" para as minhas */
  author: ReactNode;
  timestamp: ReactNode;
  /** os selos em texto: "Nota interna", "Liderança", "Sistema" */
  badges?: ReactNode;
  /** a pastilha de quem falou — só aparece do lado dos outros */
  avatar?: ReactNode;
  children: ReactNode;
  /** o que vai embaixo do texto, dentro do balão — os anexos */
  footer?: ReactNode;
}) {
  if (side === 'sistema') {
    return (
      <li className="flex justify-center">
        <div className="max-w-[85%] rounded-pilula bg-superficie-suave px-3 py-1.5 text-center text-xs text-texto-suave">
          <span className="whitespace-pre-wrap">{children}</span>
          <span className="ml-1.5 text-texto-tenue">· {timestamp}</span>
          {badges && <span className="ml-1.5 inline-flex gap-1 align-middle">{badges}</span>}
          {footer}
        </div>
      </li>
    );
  }

  const mine = side === 'meu';

  return (
    <li className={cn('flex gap-2.5', mine && 'flex-row-reverse')}>
      {!mine && avatar}

      <div className={cn('flex min-w-0 max-w-[85%] flex-col sm:max-w-[75%]', mine ? 'items-end' : 'items-start')}>
        <div className={cn('flex flex-wrap items-center gap-2', mine && 'justify-end')}>
          <span className="text-[0.8125rem] font-bold">{author}</span>
          <span className="text-[0.71875rem] text-texto-tenue">{timestamp}</span>
          {badges}
        </div>

        <div
          className={cn(
            'mt-1 rounded-grande border px-3.5 py-2.5',
            /* o canto de onde a fala sai, como num balão de chat */
            mine ? 'rounded-tr-pequeno' : 'rounded-tl-pequeno',
            /* a ordem é a regra: visibilidade (nota) antes de autoria */
            tone === 'nota'
              ? 'border-alerta bg-alerta-suave'
              : mine
                ? cn('bg-primaria-suave', tone === 'lideranca' ? 'border-primaria' : 'border-transparent')
                : tone === 'lideranca'
                  ? 'border-primaria bg-superficie'
                  : 'border-borda bg-superficie-suave',
          )}
        >
          <div className="whitespace-pre-wrap text-sm">{children}</div>
          {footer}
        </div>
      </div>
    </li>
  );
}
